import { useQuery } from "@apollo/client";
import { ReactElement, useMemo, useState } from "react";
import {
  GetOrganizationLocationsQueryQuery,
  GetOrganizationLocationsQueryQueryVariables,
} from "types/graphql-schema";

import { currentOrganizationVar } from "@cache/cache";
import ComboboxGeneric, {
  ComboboxGenericButtonProps,
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import getOrganizationLocationsQuery from "@components/location-dropdown/get-organization-locations-query";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

type OrganizationLocationDropdownProps = {
  excludeLocations: string[];
  onChangeLocation: (location: string) => void;
  children?: ReactElement<ComboboxGenericButtonProps>;
  placeholder?: string;
};

const OrganizationLocationDropdown = ({
  excludeLocations,
  onChangeLocation,
  children,
  placeholder,
}: OrganizationLocationDropdownProps) => {
  const currentOrganization = currentOrganizationVar();
  const [canLoadData, setCanLoadData] = useState(false);

  const { data: organizationData, loading } = useQuery<
    GetOrganizationLocationsQueryQuery,
    GetOrganizationLocationsQueryQueryVariables
  >(getOrganizationLocationsQuery, {
    variables: { organizationId: currentOrganization.id },
    skip: !canLoadData,
  });

  const options: ComboboxGenericOption<string>[] = useMemo(() => {
    const availableLocations =
      organizationData?.organization?.availableLocations;

    if (!availableLocations) {
      return [];
    }

    return availableLocations
      .filter((location: string | null): location is string => !!location)
      .map((location: string) => ({
        value: location,
        label: location,
      }));
  }, [organizationData]);

  const filteredOptions = options.filter(
    (option) => !excludeLocations.includes(option.value),
  );

  const handleChangeLocation = (
    option: ComboboxGenericOption<string> | null,
  ) => {
    if (option?.value) {
      onChangeLocation(option.value);
    }
  };

  const handlePreloadData = () => {
    setCanLoadData(true);
  };

  return (
    <ComboboxGeneric
      aria-label="Location dropdown"
      loading={loading}
      value={null}
      options={filteredOptions}
      onChangeValue={handleChangeLocation}
      onClickButton={handlePreloadData}
      placeholder={placeholder}
    >
      {children ? (
        children
      ) : (
        <ComboboxGeneric.Button>
          <TopicflowIcon type="plus" className="h-4 w-4" />
          {placeholder ? placeholder : "Pick a location"}
        </ComboboxGeneric.Button>
      )}
    </ComboboxGeneric>
  );
};

export default OrganizationLocationDropdown;
